import { useEffect, useRef, useState } from "react";
import type { RejectReason, UploadController, UploadPhase, UploadProgress, UploadResult } from "../api/types";
import { ApiError, UploadCancelledError } from "../api/types";
import { formatFileSize } from "../utils/format";
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_BYTES_LABEL } from "../utils/uploadLimits";

export interface ActiveUpload {
  file: File;
  phase: UploadPhase;
  loaded: number;
  total: number | null;
}

/** Backs the "This file was not uploaded" dialog (ADR 0011 section 5): a 422 upload_rejected, with nothing stored anywhere. */
export interface UploadRejectedDetails {
  name: string;
  reason: RejectReason;
  /** The server's fixed message for `reason`; always says nothing was stored. */
  message: string;
  reference: string;
}

export interface UseFileUploadResult {
  upload: ActiveUpload | null;
  error: string | null;
  /** Set on a 201 UploadResult with `renamedFrom`: "Saved as ...". Cleared on the next upload. */
  notice: string | null;
  /** Set on a 422 upload_rejected: opens the "This file was not uploaded" dialog. */
  rejected: UploadRejectedDetails | null;
  /** Set only on a 429 (too_many_transfers/too_many_uploads) or a 503 (scan_busy/scan_unavailable/storage_unavailable): the file stays "selected" so the user can retry without picking it again. */
  retryFile: File | null;
  start: (file: File) => void;
  cancel: () => void;
  dismissRejected: () => void;
}

/** Error codes that keep the file around for a one-click Retry, instead of just showing an error (ADR 0008 section 1, ADR 0011 section 7): all of them mean "nothing was stored, try the exact same upload again later". */
function offersRetry(code: ApiError["code"]): boolean {
  return (
    code === "too_many_transfers" ||
    code === "too_many_uploads" ||
    code === "scan_busy" ||
    code === "scan_unavailable" ||
    code === "storage_unavailable"
  );
}

/**
 * The upload state machine shared by every upload surface in the portal:
 * the client-side size-cap hint (src/utils/uploadLimits.ts), the two
 * upload phases ADR 0011 introduces (uploading, then checking while the
 * scanner works out a verdict), the 201/renamedFrom notice, the 422
 * upload_rejected dialog, and the 429/503 "keep the file, offer Retry"
 * behaviour. Extracted from the old UploadDropzone box component so the
 * file-explorer's toolbar Upload button and whole-pane drag-and-drop can
 * both drive the same logic.
 */
export function useFileUpload(
  onUpload: (file: File, onProgress: (progress: UploadProgress) => void) => UploadController,
  onUploaded: (file: UploadResult) => void,
): UseFileUploadResult {
  const [upload, setUpload] = useState<ActiveUpload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rejected, setRejected] = useState<UploadRejectedDetails | null>(null);
  const [retryFile, setRetryFile] = useState<File | null>(null);
  const controllerRef = useRef<UploadController | null>(null);

  useEffect(
    () => () => {
      controllerRef.current?.cancel();
    },
    [],
  );

  function start(file: File) {
    setError(null);
    setNotice(null);
    setRejected(null);
    setRetryFile(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(
        `"${file.name}" is ${formatFileSize(file.size)}, over the ${MAX_UPLOAD_BYTES_LABEL} limit. Choose a smaller file.`,
      );
      return;
    }

    const controller = onUpload(file, (progress) => {
      setUpload((current) =>
        current && current.file === file
          ? { ...current, phase: progress.phase, loaded: progress.loaded, total: progress.total }
          : current,
      );
    });
    controllerRef.current = controller;
    setUpload({ file, phase: "uploading", loaded: 0, total: file.size });

    controller.result
      .then((result) => {
        controllerRef.current = null;
        setUpload(null);
        if (result.renamedFrom) {
          setNotice(`Saved as "${result.name}" because "${result.renamedFrom}" already exists here.`);
        }
        onUploaded(result);
      })
      .catch((caught: unknown) => {
        controllerRef.current = null;
        setUpload(null);
        if (caught instanceof UploadCancelledError) {
          return;
        }
        if (!(caught instanceof ApiError)) {
          setError("The upload failed. Please try again.");
          return;
        }
        if (caught.code === "upload_rejected") {
          setRejected({
            name: file.name,
            reason: caught.reason ?? "unscannable",
            message: caught.message,
            reference: caught.reference ?? "",
          });
          return;
        }
        setError(caught.message);
        if (offersRetry(caught.code)) {
          setRetryFile(file);
        }
      });
  }

  function cancel() {
    controllerRef.current?.cancel();
  }

  function dismissRejected() {
    setRejected(null);
  }

  return { upload, error, notice, rejected, retryFile, start, cancel, dismissRejected };
}
